<template>
  <div>
    <Edit
      :editDialog="editDialog"
      :editCustomer="selectedCustomer"
      @close="editDialog = false"
      @submit="getList"
    />

    <v-dialog v-model="balanceLetterDialog" max-width="480">
      <v-card>
        <v-card-title>Generate balance letters</v-card-title>
        <v-card-text>
          <p class="mb-4">
            Include only parents with this many open invoices or more.
            Parents with a credit or fewer open invoices will be excluded.
          </p>
          <v-select
            v-model="minimumPendingInvoices"
            :items="pendingInvoiceOptions"
            label="Open invoices"
            outlined
            dense
            hide-details
          />
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="balanceLetterDialog = false">Cancel</v-btn>
          <v-btn
            color="primary"
            depressed
            :loading="printingBalanceLetters"
            @click="printBalanceLetters"
          >
            Generate PDF
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-row class="list-metrics">
      <v-col cols="6" sm="4" md="3">
        <div class="list-metric">
          <div class="list-metric__icon">
            <v-icon color="primary" size="21">mdi-account-group-outline</v-icon>
          </div>
          <div>
            <div class="list-metric__value">{{ count | number }}</div>
            <div class="list-metric__label">Total parents</div>
          </div>
        </div>
      </v-col>
      <v-col cols="6" sm="4" md="3">
        <div class="list-metric">
          <div class="list-metric__icon">
            <v-icon color="primary" size="21">mdi-cash-clock</v-icon>
          </div>
          <div>
            <div class="list-metric__value">{{ fullTotal | currency }}</div>
            <div class="list-metric__label">Outstanding balance</div>
          </div>
        </div>
      </v-col>
    </v-row>

    <v-card class="list-card" elevation="0">
      <div class="list-toolbar">
        <v-text-field
          v-model="search"
          class="list-toolbar__search"
          outlined
          prepend-inner-icon="mdi-magnify"
          dense
          background-color="#F8FAF8"
          placeholder="Search by name, phone, or parent ID"
          single-line
          hide-details
          clearable
        />
        <div class="list-toolbar__actions">
          <v-switch
            v-model="ShowBalanceOnly"
            color="primary"
            inset
            hide-details
            label="Balance only"
          />
          <v-btn
            icon
            outlined
            color="primary"
            :loading="loading"
            aria-label="Refresh parents"
            @click="refresh"
          >
            <v-icon size="19">mdi-refresh</v-icon>
          </v-btn>
          <v-menu bottom left offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                icon
                outlined
                color="primary"
                aria-label="More parent list actions"
              >
                <v-icon>mdi-dots-horizontal</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item
                :disabled="loading || printingBalanceLetters"
                @click="balanceLetterDialog = true"
              >
                <v-list-item-icon>
                  <v-progress-circular
                    v-if="printingBalanceLetters"
                    indeterminate
                    color="primary"
                    size="19"
                    width="2"
                  />
                  <v-icon v-else size="19">mdi-file-pdf-box</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Print balance letters</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>

      <v-alert v-if="loadError" type="warning" text tile class="ma-0">
        Parents could not be loaded. Refresh to try again.
      </v-alert>

      <v-data-table
        v-if="$vuetify.breakpoint.mdAndUp"
        :server-items-length="count"
        :footer-props="{ 'items-per-page-options': [10, 25, 35, 50] }"
        :headers="headers"
        :items="customers"
        :loading="loading"
        :options.sync="options"
        class="list-table parents-table"
      >
        <template v-slot:item="{ item }">
          <tr class="list-table-row parent-row" @click="viewProfile(item)">
            <td>
              <button class="parent-id" @click.stop="viewProfileNewTab(item)">
                #{{ item.localId }}
              </button>
            </td>
            <td>
              <div class="parent-name">{{ item.name }}</div>
              <div class="status-line">
                <span v-if="item.alert" class="status-label"
                  ><v-icon color="primary" size="12"
                    >mdi-alert-circle-outline</v-icon
                  >
                  Note</span
                >
                <span v-if="paymentLabel(item)" class="status-label"
                  ><v-icon color="primary" size="12"
                    >mdi-credit-card-outline</v-icon
                  >
                  {{ paymentLabel(item) }}</span
                >
              </div>
            </td>
            <td>
              <div class="phone-line">
                <span>Dad</span
                ><strong>{{ item.fatherPhoneNumber || "—" }}</strong>
              </div>
              <div class="phone-line">
                <span>Mom</span
                ><strong>{{ item.motherPhoneNumber || "—" }}</strong>
              </div>
            </td>
            <td class="text-center">
              <span class="list-count-badge">{{ item.studentCount || 0 }}</span>
            </td>
            <td class="text-right">
              <span :class="{ 'balance-due': Number(item.balance) > 0 }">{{
                item.balance | currency
              }}</span>
            </td>
            <td class="text-right" @click.stop>
              <v-menu bottom left offset-y>
                <template v-slot:activator="{ on, attrs }"
                  ><v-btn
                    v-bind="attrs"
                    v-on="on"
                    icon
                    aria-label="Parent actions"
                    ><v-icon>mdi-dots-horizontal</v-icon></v-btn
                  ></template
                >
                <v-list dense>
                  <v-list-item @click="viewProfile(item)"
                    ><v-list-item-icon
                      ><v-icon size="19"
                        >mdi-account-outline</v-icon
                      ></v-list-item-icon
                    ><v-list-item-title
                      >View profile</v-list-item-title
                    ></v-list-item
                  >
                  <v-list-item
                    v-if="checkRightStatus(7)"
                    @click="editCustomer(item)"
                    ><v-list-item-icon
                      ><v-icon size="19"
                        >mdi-pencil-outline</v-icon
                      ></v-list-item-icon
                    ><v-list-item-title>{{
                      $t("buttons.edit")
                    }}</v-list-item-title></v-list-item
                  >
                  <DeleteCustomer
                    v-if="checkRightStatus(8)"
                    :Customer="item"
                    @submit="getList"
                  />
                </v-list>
              </v-menu>
            </td>
          </tr>
        </template>
        <template v-slot:no-data
          ><div class="list-empty-state">
            <v-icon size="34" color="#A5B0A5"
              >mdi-account-search-outline</v-icon
            >
            <div>No parents found</div>
            <small>Try a different search or filter.</small>
          </div></template
        >
      </v-data-table>

      <v-data-iterator
        v-else
        :server-items-length="count"
        :items="customers"
        :loading="loading"
        :options.sync="options"
        :footer-props="{ 'items-per-page-options': [10, 25] }"
        class="list-mobile"
      >
        <template v-slot:default="{ items }">
          <button
            v-for="item in items"
            :key="item.id"
            class="list-mobile-card mobile-parent-card"
            type="button"
            @click="viewProfile(item)"
          >
            <div class="list-mobile-card__top">
              <div class="parent-avatar">{{ initials(item.name) }}</div>
              <div class="list-mobile-card__identity">
                <strong>{{ item.name }}</strong
                ><span>#{{ item.localId }}</span>
              </div>
              <v-icon color="#9AA59A">mdi-chevron-right</v-icon>
            </div>
            <div class="mobile-phone-block">
              <div class="phone-line">
                <span>Dad</span
                ><strong>{{ item.fatherPhoneNumber || "—" }}</strong>
              </div>
              <div class="phone-line">
                <span>Mom</span
                ><strong>{{ item.motherPhoneNumber || "—" }}</strong>
              </div>
            </div>
            <div class="list-mobile-card__grid">
              <div>
                <span>Students</span
                ><strong class="mobile-student-count">{{
                  item.studentCount || 0
                }}</strong>
              </div>
              <div>
                <span>Balance</span
                ><strong :class="{ 'balance-due': Number(item.balance) > 0 }">{{
                  item.balance | currency
                }}</strong>
              </div>
            </div>
          </button>
        </template>
        <template v-slot:no-data
          ><div class="list-empty-state">
            <v-icon size="34" color="#A5B0A5"
              >mdi-account-search-outline</v-icon
            >
            <div>No parents found</div>
            <small>Try a different search or filter.</small>
          </div></template
        >
      </v-data-iterator>
    </v-card>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import customerService from "../service.js";
import studentService from "@/modules/Student/service.js";
import invoiceService from "@/modules/Invoice/service.js";
import { createBalanceLettersPdfBlob } from "@/modules/Invoice/components/balanceLetterPdf";
import DeleteCustomer from "./DeleteCustomer";
const Edit = () => import("./Edit");

export default {
  name: "parent-list",
  components: { Edit, DeleteCustomer },
  filters: {
    number(value) {
      return new Intl.NumberFormat("en-US").format(Number(value || 0));
    },
  },
  data() {
    return {
      ShowBalanceOnly: false,
      editDialog: false,
      selectedCustomer: null,
      fullTotal: 0,
      customers: [],
      loading: true,
      printingBalanceLetters: false,
      balanceLetterDialog: false,
      minimumPendingInvoices: 2,
      pendingInvoiceOptions: [
        { text: "1 open invoice or more", value: 1 },
        { text: "2 open invoices or more", value: 2 },
        { text: "3 open invoices or more", value: 3 },
        { text: "4 open invoices or more", value: 4 },
        { text: "5 open invoices or more", value: 5 },
      ],
      loadError: false,
      count: 0,
      search: "",
      searchTimer: null,
      requestId: 0,
      headers: [
        { text: "ID", value: "localId", width: 90 },
        { text: "Parent", value: "name" },
        { text: "Phone", value: "fatherPhoneNumber", sortable: false },
        {
          text: "Students",
          value: "studentCount",
          align: "center",
          sortable: false,
          width: 100,
        },
        { text: "Balance", value: "balance", align: "right", width: 150 },
        { text: "", value: "actions", sortable: false, width: 64 },
      ],
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ["name"],
        sortDesc: [false],
      },
    };
  },
  computed: {
    ...mapGetters("global", ["checkRightStatus", "currentBusiness"]),
  },
  watch: {
    ShowBalanceOnly() {
      this.options.page = 1;
      this.getList();
    },
    search() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.options.page = 1;
        this.getList();
      }, 350);
    },
    options: {
      handler() {
        this.getList();
      },
      deep: true,
    },
  },
  created() {
    if (this.$route.query && this.$route.query.ShowBalanceOnly)
      this.ShowBalanceOnly = true;
    this.getList();
    this.getOutstandingBalance();
    this.$events.listen("customerListEvent", this.refresh);
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
    this.$events.remove("customerListEvent");
  },
  methods: {
    ...mapActions("customer", ["setCustomerList"]),
    initials(name) {
      return (name || "P")
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0])
        .join("")
        .toUpperCase();
    },
    paymentLabel(item) {
      if (item.payingCash) return "Cash";
      if (item.stripeId) return "Stripe";
      if (item.mohidId) return "Mohid";
      if (item.MerchantCustomers && item.MerchantCustomers.length)
        return "Online";
      return "";
    },
    viewProfile(item) {
      this.$router.push({ path: `/parents/${item.id}/profile` });
    },
    viewProfileNewTab(item) {
      const route = this.$router.resolve({
        path: `/parents/${item.id}/profile`,
      });
      window.open(route.href, "_blank");
    },
    editCustomer(item) {
      this.selectedCustomer = item;
      this.editDialog = true;
    },
    refresh() {
      this.getList();
      this.getOutstandingBalance();
    },
    getOutstandingBalance() {
      return customerService.getOutstandingBalance().then((response) => {
        this.fullTotal = response.data;
      });
    },
    getList() {
      const currentRequest = ++this.requestId;
      this.loading = true;
      this.loadError = false;
      const { sortBy, sortDesc, page, itemsPerPage } = this.options;
      const params = {
        limit: itemsPerPage,
        sortBy: sortBy[0],
        sortDesc: sortDesc[0],
      };
      if (this.ShowBalanceOnly) params.hideZero = true;
      if (this.search && this.search.length >= 3) params.search = this.search;
      else
        params.offset =
          itemsPerPage > 0 ? itemsPerPage * page - itemsPerPage : 0;
      return customerService
        .getAll(params)
        .then((response) => {
          if (currentRequest !== this.requestId) return;
          this.customers = response.data || [];
          this.count = response.count || 0;
          this.setCustomerList(this.customers);
        })
        .catch(() => {
          if (currentRequest === this.requestId) this.loadError = true;
        })
        .finally(() => {
          if (currentRequest === this.requestId) this.loading = false;
        });
    },
    async printBalanceLetters() {
      const pdfWindow = window.open("", "_blank");
      if (!pdfWindow) {
        this.$swal("Popup blocked", "Please allow popups to open the balance-letter PDF.", "warning");
        return;
      }

      pdfWindow.document.title = "Preparing balance letters";
      pdfWindow.document.body.textContent = "Preparing balance letters…";
      this.printingBalanceLetters = true;

      try {
        const [customerResponse, studentResponse, invoiceResponse] = await Promise.all([
          customerService.getAll({ hideZero: true, limit: 10000, sortBy: "name", sortDesc: false }),
          studentService.getAll({ limit: 10000 }),
          invoiceService.getAll({ due: true, showCustomersOnly: true, limit: 10000 }),
        ]);
        const pendingInvoiceCounts = (invoiceResponse.data || []).reduce((counts, invoice) => {
          if (invoice.CustomerId) counts[invoice.CustomerId] = (counts[invoice.CustomerId] || 0) + 1;
          return counts;
        }, {});
        const customers = (customerResponse.data || []).filter(customer =>
          Number(customer.balance) > 0 &&
          (pendingInvoiceCounts[customer.id] || 0) >= this.minimumPendingInvoices
        );
        const students = studentResponse.data || [];
        const studentsByCustomer = students.reduce((groups, student) => {
          if (!groups[student.CustomerId]) groups[student.CustomerId] = [];
          groups[student.CustomerId].push(student);
          return groups;
        }, {});

        if (!customers.length) {
          throw new Error(`No positive-balance parents have ${this.minimumPendingInvoices} open invoices or more`);
        }
        if (!this.currentBusiness) throw new Error("Business information is unavailable");

        const blob = await createBalanceLettersPdfBlob({
          business: this.currentBusiness,
          letters: customers.map(customer => ({
            customer,
            students: studentsByCustomer[customer.id] || [],
          })),
          date: this.$moment().format("MMM DD YYYY"),
        });
        pdfWindow.location.replace(URL.createObjectURL(blob));
        this.balanceLetterDialog = false;
      } catch (error) {
        pdfWindow.close();
        this.$swal("Could not create letters", error.message || "Please try again.", "error");
      } finally {
        this.printingBalanceLetters = false;
      }
    },
  },
};
</script>

<style scoped>
/* Shared metrics, card, toolbar, search, table, mobile cards,
   count badge, and empty-state styles come from app.scss. */

.parent-row {
  cursor: pointer;
}

.parent-id {
  padding: 3px 0;
  color: #388e3c;
  font-weight: 650;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.parent-name {
  color: #253047;
  font-weight: 650;
}

.phone-line {
  display: flex;
  min-height: 21px;
  align-items: baseline;
  gap: 8px;
}

.phone-line span {
  width: 28px;
  color: #8a968d;
  font-size: 10px;
  font-weight: 650;
  text-transform: uppercase;
}

.phone-line strong {
  color: #445047;
  font-size: 12px;
  font-weight: 550;
}

.status-line {
  display: flex;
  min-height: 16px;
  margin-top: 3px;
  gap: 10px;
}

.status-label {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  color: #718076;
  font-size: 10px;
}

.balance-due {
  color: #2e7d32;
  font-weight: 700;
}

.list-toolbar__actions ::v-deep .v-input--switch {
  margin: 0;
  padding: 0;
}

.parent-avatar {
  display: flex;
  width: 40px;
  height: 40px;
  margin-right: 11px;
  align-items: center;
  justify-content: center;
  color: #2e7d32;
  border-radius: 50%;
  background: #eaf6eb;
  font-size: 13px;
  font-weight: 750;
}

.mobile-parent-card {
  display: block;
  width: 100%;
  color: inherit;
  text-align: left;
  border: 0;
  background: white;
}

.mobile-phone-block {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin: 14px 0 0 51px;
}

.mobile-phone-block .phone-line {
  align-items: flex-start;
  flex-direction: column;
  gap: 1px;
}

.mobile-phone-block .phone-line span {
  width: auto;
}

.mobile-student-count {
  color: #2e7d32 !important;
  font-size: 17px !important;
  font-weight: 800 !important;
}

@media (max-width: 599px) {
  .list-toolbar__actions {
    justify-content: space-between;
  }

  .list-toolbar__actions ::v-deep .v-label {
    font-size: 13px;
  }
}
</style>
